import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getClientUsersWithDetailsAsync, changeUserRoleAsync } from '../../../Services/AdminService';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';
import Modal from '../../../Shared/Components/Modal/Modal';
import moment from 'moment';
import { formatDateTime } from '../../../Infrastructure/Utilities/DateHelper';

const ClientUsersManagement = ({ clientId }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');
    const [groupFilter, setGroupFilter] = useState('All');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userToChangeRole, setUserToChangeRole] = useState(null);
    const [uniqueGroups, setUniqueGroups] = useState([]);

    useEffect(() => {
        if (clientId) {
            fetchUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    useEffect(() => {
        filterUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, searchTerm, roleFilter, groupFilter]);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const response = await getClientUsersWithDetailsAsync(clientId);
            setUsers(response.users || []);

            // Extract unique groups
            const groups = [...new Set(response.users
                .filter(u => u.groupName)
                .map(u => u.groupName))];
            setUniqueGroups(groups);
        } catch (error) {
            console.error('Error fetching users:', error);
            showToast(false, 'Failed to load users');
        } finally {
            setIsLoading(false);
        }
    };

    const filterUsers = () => {
        let filtered = [...users];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(u =>
                `${u.firstName} ${u.lastName} ${u.email}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );
        }

        // Role filter
        if (roleFilter !== 'All') {
            filtered = filtered.filter(u => u.role === roleFilter);
        }

        // Group filter
        if (groupFilter !== 'All') {
            filtered = filtered.filter(u => u.groupName === groupFilter);
        }

        setFilteredUsers(filtered);
    };

    const handleRoleChange = async () => {
        if (!userToChangeRole) return;

        const newRole = userToChangeRole.role === 'Learner' ? 'ClientAdmin' : 'Learner';

        try {
            const response = await changeUserRoleAsync({
                userId: userToChangeRole.userId,
                newRole: newRole
            });

            if (response.success) {
                showToast(true, `User role changed to ${newRole}`);
                fetchUsers(); // Refresh the list
            } else {
                showToast(false, response.errors?.[0]?.error || 'Failed to change role');
            }
        } catch (error) {
            console.error('Error changing role:', error);
            showToast(false, 'Failed to change role');
        }
    };

    const getRoleBadgeColor = (role) => {
        return role === 'ClientAdmin' ? '#3b82f6' : '#10b981';
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Completed':
                return '#10b981';
            case 'In Progress':
                return '#f59e0b';
            case 'Not Started':
                return '#6b7280';
            default:
                return '#6b7280';
        }
    };

    const getSourceIcon = (source) => {
        switch (source) {
            case 'Direct':
                return '👤';
            case 'Group':
                return '👥';
            case 'Client':
                return '🏢';
            case 'Pathway':
                return '🎓';
            default:
                return '📚';
        }
    };

    const getSourceBadgeColor = (source) => {
        switch (source) {
            case 'Direct':
                return '#8b5cf6';
            case 'Group':
                return '#3b82f6';
            case 'Client':
                return '#10b981';
            case 'Pathway':
                return '#f59e0b';
            default:
                return '#6b7280';
        }
    };

    return (
        <>
            {/* Change Role Confirmation Modal */}
            <Modal
                modalId="changeRoleModal"
                title="Change User Role"
                bodyText={userToChangeRole ? (
                    <div>
                        <p>Are you sure you want to change <strong>{userToChangeRole.firstName} {userToChangeRole.lastName}</strong>'s role?</p>
                        <p>Current Role: <strong>{userToChangeRole.role}</strong></p>
                        <p>New Role: <strong>{userToChangeRole.role === 'Learner' ? 'ClientAdmin' : 'Learner'}</strong></p>
                    </div>
                ) : ''}
                buttonText="Change Role"
                buttonFunc={handleRoleChange}
            />

            {/* View User Courses Modal */}
            {selectedUser && (
                <div className="modal fade" id="viewCoursesModal" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Courses for {selectedUser.firstName} {selectedUser.lastName}
                                    <div style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#64748b' }}>
                                        Registered: {formatDateTime(selectedUser.createdDate)}
                                    </div>
                                </h5>
                                <span className="fa fa-times clickable" data-dismiss="modal" aria-label="Close"></span>
                            </div>
                            <div className="modal-body">
                                {selectedUser.assignedCourses.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {selectedUser.assignedCourses.map((course) => (
                                            <div
                                                key={course.courseId}
                                                style={{
                                                    padding: '16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    background: '#f8fafc'
                                                }}
                                            >
                                                <div style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '600',
                                                    marginBottom: '8px',
                                                    color: '#1e293b'
                                                }}>
                                                    {course.courseTitle}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        background: course.hasAssessment ? '#0ea5e9' : '#64748b',
                                                        color: 'white'
                                                    }}>
                                                        {course.hasAssessment ? 'Assessment' : 'Mindfullness'}
                                                    </span>
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        background: getSourceBadgeColor(course.assignmentSource),
                                                        color: 'white'
                                                    }}>
                                                        {getSourceIcon(course.assignmentSource)} {course.assignmentSource}
                                                    </span>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        background: getStatusBadgeColor(course.status),
                                                        color: 'white'
                                                    }}>
                                                        {course.status}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                    via {course.sourceName}
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                                                    Started: {formatDateTime(course.startedDateTime)}
                                                    {' · '}
                                                    Completed: {formatDateTime(course.completedDateTime)}
                                                </div>
                                                {course.progress != null && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>
                                                            Progress: {course.progress}%
                                                        </div>
                                                        <div style={{
                                                            width: '100%',
                                                            height: '8px',
                                                            background: '#e5e7eb',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden'
                                                        }}>
                                                            <div style={{
                                                                width: `${course.progress}%`,
                                                                height: '100%',
                                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                transition: 'width 0.3s'
                                                            }} />
                                                        </div>
                                                    </div>
                                                )}
                                                {course.score != null && (
                                                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                                                        Score: {course.score}%
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                        No courses assigned to this user
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                {selectedUser.urlToken ? (
                                    <Link
                                        to={`/learner/${selectedUser.urlToken}`}
                                        className="btn btn-primary mr-auto"
                                        onClick={() => {
                                            // Close modal using vanilla JavaScript
                                            const modal = document.getElementById('viewCoursesModal');
                                            const backdrop = document.querySelector('.modal-backdrop');
                                            if (modal) {
                                                modal.classList.remove('show');
                                                modal.style.display = 'none';
                                                document.body.classList.remove('modal-open');
                                            }
                                            if (backdrop) {
                                                backdrop.remove();
                                            }
                                        }}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none'
                                        }}
                                    >
                                        📚 Manage Course Assignments
                                    </Link>
                                ) : (
                                    <div className="mr-auto" style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                                        ⚠️ User urlToken missing - Backend API needs update
                                    </div>
                                )}
                                <button type="button" className="btn btn-secondary" data-dismiss="modal">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div>
                {/* Filters Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '2px solid #e5e7eb',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#64748b',
                                marginBottom: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                🔍 Search Users
                            </label>
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: '0.95rem',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    outline: 'none'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#64748b',
                                marginBottom: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                👤 Filter by Role
                            </label>
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: '0.95rem',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    background: 'white'
                                }}
                            >
                                <option value="All">All Roles</option>
                                <option value="Learner">Learner</option>
                                <option value="ClientAdmin">Client Admin</option>
                            </select>
                        </div>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#64748b',
                                marginBottom: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                👥 Filter by Group
                            </label>
                            <select
                                value={groupFilter}
                                onChange={(e) => setGroupFilter(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: '0.95rem',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    background: 'white'
                                }}
                            >
                                <option value="All">All Groups</option>
                                {uniqueGroups.map(group => (
                                    <option key={group} value={group}>{group}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{
                        marginTop: '12px',
                        fontSize: '0.85rem',
                        color: '#64748b'
                    }}>
                        Showing {filteredUsers.length} of {users.length} users
                    </div>
                </div>

                {/* Users Table */}
                {isLoading ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{ fontSize: '1.2rem', color: '#64748b' }}>
                            Loading users...
                        </div>
                    </div>
                ) : filteredUsers.length > 0 ? (
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Name
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Email
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Role
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Group
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Courses
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Registered
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                            Last Login
                                        </th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b', width: '200px' }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((user, index) => (
                                        <tr key={user.userId} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
                                                <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                                    {user.firstName} {user.lastName}
                                                </div>
                                                {user.isArchived && (
                                                    <span style={{
                                                        fontSize: '0.75rem',
                                                        color: '#ef4444',
                                                        fontWeight: '600'
                                                    }}>
                                                        (Archived)
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', color: '#64748b' }}>
                                                {user.email}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    background: getRoleBadgeColor(user.role),
                                                    color: 'white'
                                                }}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', color: '#64748b' }}>
                                                {user.groupName || '-'}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                                                <button
                                                    data-toggle="modal"
                                                    data-target="#viewCoursesModal"
                                                    onClick={() => setSelectedUser(user)}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '6px 12px',
                                                        fontSize: '0.85rem',
                                                        cursor: 'pointer',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    📚 {user.assignedCourses.length} courses
                                                </button>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', color: '#64748b' }}>
                                                {formatDateTime(user.createdDate)}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', color: '#64748b' }}>
                                                {user.lastLogin ? moment(user.lastLogin).format('MMM DD, YYYY') : 'Never'}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    {user.urlToken && (
                                                        <Link
                                                            to={`/learner/${user.urlToken}`}
                                                            style={{
                                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '6px 12px',
                                                                fontSize: '0.85rem',
                                                                cursor: 'pointer',
                                                                fontWeight: '600',
                                                                textDecoration: 'none',
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            👁️ View
                                                        </Link>
                                                    )}
                                                    <button
                                                        data-toggle="modal"
                                                        data-target="#changeRoleModal"
                                                        onClick={() => setUserToChangeRole(user)}
                                                        style={{
                                                            background: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '6px 12px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        🔄 Role
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{ fontSize: '1.2rem', color: '#64748b', marginBottom: '8px' }}>
                            No users found
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
                            Try adjusting your filters
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ClientUsersManagement;
